import { useEffect, useMemo, useRef, useState, type JSX, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { defaultLayout, type LayoutSchemaV1, type LayoutTemplateId, type StepSchema, type ExtendedCertificate } from 'shared';
import type { WaitingState } from '../../engine/stepEngine';
import { resolveMediaRefToUrl } from '../../utils/mediaRef';
import { EASE_EMPHASIS, EASE_BACK_IN } from '../../utils/motion';
import type { RunState } from 'shared';
import { EndingView } from './EndingView';
import { LineDialogView } from '../line-dialog/LineDialogView';
import { LineDialogPanelShell } from '../line-dialog/LineDialogPanelShell';
import { audioEngine } from '../../audio/audioEngine';
import { vibratePreset } from '../../audio/haptics';
import { readDomMood } from '../../minigames/foundation/hooks/readDomMood';
import { useProductionMode } from '../../utils/productionMode';

/** P7-1: dialog 自動送りタイマーの進行状況（App 層のタイマーを描画に反映するためのビュー用データ）。
 *  armKey が変わるたびにヘアラインのアニメーションをリセットする（行が変わった＝再アーム）。 */
export interface AutoAdvanceProgress {
  armKey: string;
  delayMs: number;
}

/** P7-3: veil の「表示保持」時間（ms）。入り→保持→抜けの合計は CSS 変数
 *  --step-transition-ms（style.css 側）で定義される transition 時間と合わせて
 *  200-350ms 程度に収まるよう調整している。この定数は JS 側のタイマー（抜け開始）専用で、
 *  実際の見え方（フェードの速さ）は CSS transition が担う。 */
const VEIL_HOLD_MS = 90;
/** P7-3: 背景クロスディゾルブの DOM クリーンアップ猶予（ms）。
 *  CSS 側の --bg-dissolve-ms（トランジション時間）より確実に長く取り、
 *  transition 完了前に旧背景ノードが消えて見た目が破綻しないようにする。 */
const BG_DISSOLVE_CLEANUP_MS = 650;
/** P7-5: dialog/ai_dialog 文字送り（typewriter reveal）の 1 文字あたりの表示間隔（ms）。
 *  「いま誰かが語っている」時間感覚を作るための調整可能な定数（作者制御は今回スコープ外）。 */
const TYPEWRITER_MS_PER_CHAR = 35;
/** P8-1: タップ送り/文字送りスキップの微小フィードバック音量（相対 0..1）。
 *  頻発する音なので最小限に絞る（tap の基準 gain 0.32 に乗算＝実効ピーク ~0.05）。
 *  耳障りに感じたらまずここを下げる。ミュートは engine 側が一元支配。 */
const TAP_ADVANCE_SFX_VOLUME = 0.18;
const REVEAL_SKIP_SFX_VOLUME = 0.14;

/** P7-5: dialog/ai_dialog の「いま tap したら完了できる reveal 制御」を親（StepLayoutRenderer）に
 *  伝えるためのハンドル。DialogView がレンダーごとに最新の isRevealing/completeReveal を積む。
 *  handleStageClick はクリック時点でこの ref を読み、reveal 中なら completeReveal を呼んで
 *  advance を握りつぶす（2段階タップ）。 */
interface DialogRevealController {
  isRevealing: boolean;
  completeReveal: () => void;
}

export interface StepLayoutRendererProps {
  step: StepSchema | null;
  waiting: WaitingState | null;
  state?: RunState | null;
  canAdvance?: boolean;
  onRequestAdvance?: () => void;
  /** P7-1: dialog の自動送りタイマーがアーム中なら進行状況を渡す（非アーム時は null/undefined） */
  autoAdvanceProgress?: AutoAdvanceProgress | null;
  onSceneVideoEnded?: (ended: boolean) => void;
  onEndingAction?: (action: 'replay' | 'home' | 'url', url?: string) => void;
  onGenerateCertificate?: () => Promise<ExtendedCertificate>;
  /** エンディング到達時にランを完了としてマークするコールバック */
  onCompleteEnding?: (endingId?: string) => Promise<void>;
  /** P9-1: このプレイスルーを一意に識別する id。図鑑記録（EndingView）の冪等キーに使う。 */
  playthroughId?: string;
  /** 図鑑を現在の物語だけに絞るためのシナリオ id。未指定時は従来どおり全体台帳を表示する。 */
  scenarioId?: string;
  /** LINE Dialog: 選択肢選択 */
  onLineDialogChoiceSelect?: (choiceIndex?: number) => void;
  /** LINE Dialog: 自由入力送信 */
  onLineDialogFreeInputSubmit?: (text: string) => void;
  /** LINE Dialog: AI返信アニメーション完了後 */
  onLineDialogComplete?: () => void;
  /** P7-5: dialog/ai_dialog の文字送りが完了しているか（reveal 未完なら false）が変わるたびに呼ばれる。
   *  App 層はこれを autoAdvance の起算条件に使う（文字送り中はタイマーをアームしない）。
   *  dialog/ai_dialog 以外の step では常に true（自動送り対象外の判定は App 側の既存条件に委ねる）。 */
  onDialogRevealDoneChange?: (revealDone: boolean) => void;
}

/**
 * 2026-07-20 傷②: hooks 規約違反の修正。
 * 旧実装は「if (!step) return <準備中>」の early return が後続の全 hooks（useRef/useState/
 * useEffect/useMemo）より前にあり、step が null ↔ 非 null に切り替わるレンダー間でフック数が
 * 変わって React が throw（Rendered fewer/more hooks）→ root に ErrorBoundary が無く白画面に
 * 直結していた（例: reward nextAction='end' で moveToNode(null) → snapshot.step が null になる遷移）。
 *
 * 修正は「hooks を一切持たない外殻 + step 非 null を要求する本体」への分割。
 * - 外殻（StepLayoutRenderer）はフック 0 個なので、null 分岐してもフック数は常に不変（0 個）。
 * - 本体（StepLayoutRendererBody）はマウント中フック数が常に一定（従来の並びを無編集で維持）。
 *   step が null になると本体はアンマウントされ（effect cleanup が走る）、復帰時は初期状態で
 *   再マウントされる。従来この遷移は React の throw で必ず白画面だったため、内部 ref が
 *   リセットされる挙動差は「壊れていた経路が生きる」方向のみで、通常進行（step が常に非 null）は
 *   コード経路・フック順とも従来と同一。
 */
export function StepLayoutRenderer(props: StepLayoutRendererProps): JSX.Element {
  if (!props.step) {
    return (
      <div className="layout-renderer empty">
        {/* P8-3: 開発用語「Step」を出さない。端末の状態断片（S1）で表す。 */}
        <p>……準備中</p>
      </div>
    );
  }
  return <StepLayoutRendererBody {...props} step={props.step} />;
}

type StepLayoutRendererBodyProps = Omit<StepLayoutRendererProps, 'step'> & { step: StepSchema };

function StepLayoutRendererBody({
  step,
  waiting,
  state,
  canAdvance,
  onRequestAdvance,
  autoAdvanceProgress,
  onSceneVideoEnded,
  onEndingAction,
  onGenerateCertificate,
  onCompleteEnding,
  playthroughId,
  scenarioId,
  onLineDialogChoiceSelect,
  onLineDialogFreeInputSubmit,
  onLineDialogComplete,
  onDialogRevealDoneChange
}: StepLayoutRendererBodyProps): JSX.Element {
  const lastDialogBackgroundRef = useRef<CSSProperties | null>(null);
  const lastDialogMediaRef = useRef<ReturnType<typeof extractBackgroundMedia> | null>(null);
  const lastDialogCameraRef = useRef<ReturnType<typeof computeCameraTransform> | null>(null);
  const lastSceneBackgroundRef = useRef<CSSProperties | null>(null);
  const lastSceneMediaRef = useRef<ReturnType<typeof extractBackgroundMedia> | null>(null);

  // P7-3: fade-through veil の対象外判定に使う「前回の step」を覚えておく ref。
  // 新旧どちらかが minigame / video 背景の scene / line_dialog なら veil を発火させない
  // （ミニゲームの postMessage・動画再生・LINE 全画面の自前マウントアニメを邪魔しないため）。
  const prevStepRef = useRef<StepSchema | null>(null);
  const prevStepIsVideoSceneRef = useRef<boolean>(false);

  const computedBackground = buildBackgroundStyle(step, state);
  const computedMedia = extractBackgroundMedia(step, state);
  const computedCamera = computeCameraTransform(step, state);

  if (step.type === 'scene') {
    lastSceneBackgroundRef.current = computedBackground;
    lastSceneMediaRef.current = computedMedia;
  }

  let background = computedBackground;
  let media = computedMedia;
  let camera = computedCamera;

  const isPanelLineDialog = step.type === 'line_dialog' && (step as any).presentation === 'panel';

  if (step.type === 'dialog' && !computedMedia && lastSceneMediaRef.current) {
    background = lastSceneBackgroundRef.current ?? computedBackground;
    media = lastSceneMediaRef.current;
  } else if (isPanelLineDialog && lastSceneMediaRef.current) {
    // P7-4: presentation='panel' のときだけ、dialog と同様に直前の scene 背景（世界）を
    // 継承して描画する。fullscreen（既定・presentation 未指定）はここを通らないため
    // 影響ゼロ（extractBackgroundMedia が line_dialog に null を返す従来どおりの経路）。
    background = lastSceneBackgroundRef.current ?? computedBackground;
    media = lastSceneMediaRef.current;
  } else if (step.type === 'branch' && lastDialogBackgroundRef.current) {
    background = lastDialogBackgroundRef.current;
    media = lastDialogMediaRef.current;
    camera = lastDialogCameraRef.current;
  }

  if (step.type === 'dialog') {
    lastDialogBackgroundRef.current = background;
    lastDialogMediaRef.current = media;
    lastDialogCameraRef.current = camera;
  }
  const isSceneVideo = step.type === 'scene' && media?.type === 'video';
  const classes = ['layout-renderer'];
  if (waiting?.type === 'minigame') {
    classes.push('is-minigame-waiting');
  }

  // P7-1: 画面タップ送り対象の判定。
  // 「非インタラクティブな待機状態（canAdvance=true）」のうち、タップで進めてよいのは
  // dialog/ai_dialog と、確認ボタンを持たない・動画でない scene のみ。
  // - video scene は既存の video-tap-layer（再生完了後のタップ進行）と役割が重複するため除外
  //   （二重発火防止。isSceneVideo は上で判定済み）。
  // - confirmButton を持つ scene は現地照合・依頼画像確認の明示操作なので、タップ送りと
  //   併存させない（ボタンのみで進める）。
  // - free_input/spot_gate/minigame/line_dialog/branch/reward/ending は canAdvance=true に
  //   ならない（waiting が立つか、専用の送信 UI を経由する）ため対象外。
  const sceneConfirmButton =
    step.type === 'scene' ? ((step as any).config as { confirmButton?: unknown } | undefined)?.confirmButton : undefined;
  const isTapAdvanceEligible =
    (step.type === 'dialog' || step.type === 'ai_dialog') ||
    (step.type === 'scene' && !isSceneVideo && !sceneConfirmButton);
  const tapAdvanceActive = Boolean(isTapAdvanceEligible && canAdvance && onRequestAdvance);
  const isDialogStep = step.type === 'dialog' || step.type === 'ai_dialog';

  // P7-5: DialogView がレンダーごとに最新の isRevealing/completeReveal を積む先。
  // タップはクリックイベント発火時にこの ref を同期的に読むので、React state ではなく
  // ref にしている（DialogView 側からの毎レンダー更新が親の再レンダーを誘発しないようにするため）。
  const dialogRevealControllerRef = useRef<DialogRevealController | null>(null);
  const handleDialogRevealControllerChange = (controller: DialogRevealController) => {
    dialogRevealControllerRef.current = controller;
  };

  const handleStageClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (!tapAdvanceActive) return;
    // 常駐UI（音量/図鑑/現在地/LINE履歴等）や sr-only ボタン自体のクリックで誤進行させない。
    const target = event.target as HTMLElement | null;
    if (target && target.closest('button, a, input, textarea, select, [role="button"]')) {
      return;
    }
    // P7-5: dialog/ai_dialog で文字送り中なら、1回目のタップは「全文即表示」に消費し、
    // advance はしない（ADV標準の2段階タップ）。全文表示済み（isRevealing=false）なら
    // 従来どおり advance する。
    // P8-1: どちらのタップにもごく小さな tick 音 + tap ハプティクスを添える（頻発音のため最小音量・
    // mood は data-mood 追従・ミュート/音量は engine の master gain が一元支配）。
    if (isDialogStep && dialogRevealControllerRef.current?.isRevealing) {
      dialogRevealControllerRef.current.completeReveal();
      audioEngine.playSfx('tap', { mood: readDomMood(), volume: REVEAL_SKIP_SFX_VOLUME });
      vibratePreset('tap');
      return;
    }
    audioEngine.playSfx('tap', { mood: readDomMood(), volume: TAP_ADVANCE_SFX_VOLUME });
    vibratePreset('tap');
    onRequestAdvance?.();
  };

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isVideoEnded, setIsVideoEnded] = useState(false);
  const [needsTapToPlay, setNeedsTapToPlay] = useState(false);
  const [videoAudioEnabled, setVideoAudioEnabled] = useState(false);

  // ===== P7-3: fade-through veil（step 境界の暗幕ディップ） =====
  // 単一ツリー方式（旧新の並行マウントは行わない）。veil の対象外（minigame /
  // video 背景 scene / line_dialog）は下の isVeilExempt で判定する。
  const isVeilExemptStep = (s: StepSchema | null, mediaForStep: ReturnType<typeof extractBackgroundMedia> | null): boolean => {
    if (!s) return true;
    if (s.type === 'minigame' || s.type === 'line_dialog') return true;
    if (s.type === 'scene' && mediaForStep?.type === 'video') return true;
    return false;
  };

  const [veilActive, setVeilActive] = useState(false);
  const veilTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prevStep = prevStepRef.current;
    const prevExempt = prevStepIsVideoSceneRef.current;
    // 初回マウント時（prevStep が null）は veil を出さない。
    const isFirstMount = prevStep === null;
    const stepChanged = !isFirstMount && prevStep!.id !== step.id;

    // 現在 step が対象外か（media は現在描画中のもの＝上で解決済みの `media` を使う）。
    const currentExempt = isVeilExemptStep(step, media);

    // prefers-reduced-motion: reduce では即時切替（アニメなし）にする。CSS 側も
    // transition:none にしているが、JS 側でも veil 自体を発火させないことで
    // 「一瞬だけ暗幕が点滅する」ような transition なしの副作用を避ける。
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (stepChanged && !prevExempt && !currentExempt && !prefersReducedMotion) {
      // 入り: 即座に veil を表示 → reflow 後に抜けさせる（transitionend で消灯）。
      if (veilTimerRef.current !== null) {
        clearTimeout(veilTimerRef.current);
        veilTimerRef.current = null;
      }
      setVeilActive(true);
      // 抜け開始までの短い保持（暗幕が一瞬見える時間）。CSS transition の合計が
      // --step-transition-ms に収まるよう、保持時間はその一部にとどめる。
      veilTimerRef.current = setTimeout(() => {
        setVeilActive(false);
        veilTimerRef.current = null;
      }, VEIL_HOLD_MS);
    }

    prevStepRef.current = step;
    prevStepIsVideoSceneRef.current = currentExempt;

    return () => {
      if (veilTimerRef.current !== null) {
        clearTimeout(veilTimerRef.current);
        veilTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  // ===== P7-3: 背景クロスディゾルブ =====
  // 背景 media の src が「変わる時だけ」、旧背景を上に重ねて opacity ディゾルブする。
  // video 背景は対象外（従来どおり即時切替）。同一 src では何もしない（DOM 再利用のまま）。
  const backgroundDissolveKey = (m: ReturnType<typeof extractBackgroundMedia>): string | null => {
    if (!m) return null;
    if (m.type === 'image') return `image:${m.src}`;
    if (m.type === 'color') return `color:${m.color ?? ''}`;
    // video は対象外（キー化しない＝常に null 扱いで旧背景を作らない）
    return null;
  };

  const currentDissolveKey = media?.type !== 'video' ? backgroundDissolveKey(media) : null;
  const prevMediaSnapshotRef = useRef<ReturnType<typeof extractBackgroundMedia> | null>(null);
  // 初回マウント判定に使うため、未設定状態を明示的に undefined にしておく（string | null と区別する）。
  const prevDissolveKeyRef = useRef<string | null | undefined>(undefined);
  const [outgoingBg, setOutgoingBg] = useState<{
    media: ReturnType<typeof extractBackgroundMedia>;
    camera: CameraTransform | null;
  } | null>(null);
  const [outgoingBgFading, setOutgoingBgFading] = useState(false);
  const outgoingBgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prevKey = prevDissolveKeyRef.current;
    const prevMedia = prevMediaSnapshotRef.current;
    const isFirstMount = prevKey === undefined;

    // video 背景への/からの遷移は対象外（即時切替のまま）。
    const isVideoInvolved = media?.type === 'video' || prevMedia?.type === 'video';

    // prefers-reduced-motion: reduce では旧背景レイヤーを作らず即時切替のまま
    // （DOM 再利用のみ・従来挙動）にする。
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (
      !isFirstMount &&
      !isVideoInvolved &&
      !prefersReducedMotion &&
      prevKey !== null &&
      currentDissolveKey !== null &&
      prevKey !== currentDissolveKey
    ) {
      // 旧背景をスナップショットして重ね、次のフレームでフェードアウトを開始する。
      if (outgoingBgTimerRef.current !== null) {
        clearTimeout(outgoingBgTimerRef.current);
        outgoingBgTimerRef.current = null;
      }
      setOutgoingBg({ media: prevMedia, camera: lastDialogCameraRef.current ?? computedCamera });
      setOutgoingBgFading(false);
      // 次のフレームで opacity:0 へ transition させる（reflow 待ち）。
      const raf = requestAnimationFrame(() => setOutgoingBgFading(true));
      // ディゾルブ完了後に旧層を DOM から除去する（リーク防止）。
      outgoingBgTimerRef.current = setTimeout(() => {
        setOutgoingBg(null);
        setOutgoingBgFading(false);
        outgoingBgTimerRef.current = null;
      }, BG_DISSOLVE_CLEANUP_MS);
      return () => cancelAnimationFrame(raf);
    }

    prevDissolveKeyRef.current = currentDissolveKey;
    prevMediaSnapshotRef.current = media;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDissolveKey]);

  useEffect(() => {
    return () => {
      if (outgoingBgTimerRef.current !== null) clearTimeout(outgoingBgTimerRef.current);
    };
  }, []);

  const sceneVideoSrc = useMemo(
    () => (isSceneVideo && media?.type === 'video' ? media.src : null),
    [isSceneVideo, media]
  );

  useEffect(() => {
    if (!isSceneVideo) {
      setIsVideoEnded(false);
      setNeedsTapToPlay(false);
      setVideoAudioEnabled(false);
      return;
    }
    setIsVideoEnded(false);
    setNeedsTapToPlay(false);
    setVideoAudioEnabled(false);
    onSceneVideoEnded?.(false);
  }, [isSceneVideo, sceneVideoSrc]);

  useEffect(() => {
    if (!isSceneVideo) return;
    const el = videoRef.current;
    if (!el) return;

    // 可能なら「音声付き」で最初から自動再生を試みる。
    // ただしブラウザ制約でブロックされることがあるため、その場合は「動画を進めない」(pause+0秒) にして
    // ユーザーのタップで 0秒から音声付き再生を開始する（冒頭音声を聞き逃さない）。
    el.playsInline = true;
    el.autoplay = false;
    el.volume = 1;

    const tryPlayWithAudio = () => {
      try {
        el.muted = false;
        const p = el.play();
        if (p && typeof (p as Promise<void>).catch === 'function') {
          (p as Promise<void>).catch(() => {
            // 音声付きの自動再生ができない場合、動画は進めず待機してタップで開始
            setVideoAudioEnabled(false);
            setNeedsTapToPlay(true);
            try {
              el.pause();
              el.currentTime = 0;
              el.muted = true;
            } catch {
              // ignore
            }
          });
          (p as Promise<void>)
            .then(() => {
              setVideoAudioEnabled(true);
              setNeedsTapToPlay(false);
            })
            .catch(() => {
              // catchは上で処理
            });
        }
      } catch {
        setVideoAudioEnabled(false);
        setNeedsTapToPlay(true);
        try {
          el.pause();
          el.currentTime = 0;
          el.muted = true;
        } catch {
          // ignore
        }
      }
    };

    // src反映後すぐに「音声付き」再生を試みる
    tryPlayWithAudio();
  }, [isSceneVideo, sceneVideoSrc]);

  const handleVideoEnded = () => {
    if (!isSceneVideo) return;
    setIsVideoEnded(true);
    onSceneVideoEnded?.(true);
  };

  const handleTap = () => {
    if (!isSceneVideo) return;
    const el = videoRef.current;
    if (!el) return;

    if (!isVideoEnded) {
      // 音声付き自動再生がブロックされた場合、タップで「0秒から音声付き」で開始する
      if (needsTapToPlay) {
        setNeedsTapToPlay(false);
        setVideoAudioEnabled(true);
        try {
          el.currentTime = 0;
        } catch {
          // ignore
        }
        try {
          el.muted = false;
          el.volume = 1;
          void el.play();
        } catch {
          // それでも無理ならミュートで継続（ただし冒頭音声は要件上NGなので、次のタップでも再試行できるよう戻す）
          el.muted = true;
          setVideoAudioEnabled(false);
          setNeedsTapToPlay(true);
        }
      }
      return;
    }

    if (canAdvance && onRequestAdvance) {
      onRequestAdvance();
    }
  };

  if (tapAdvanceActive) {
    classes.push('is-tap-advance-active');
  }

  return (
    <div
      className={classes.join(' ')}
      style={background}
      onClick={tapAdvanceActive ? handleStageClick : undefined}
    >
      {media?.type === 'image' && (
        <div className="layout-renderer__bg">
          <img
            src={media.src}
            alt="background"
            style={camera ? { transform: camera.transform, transition: camera.transition, transformOrigin: camera.transformOrigin } : undefined}
          />
        </div>
      )}
      {media?.type === 'video' && (
        <div className="layout-renderer__bg">
          <video
            src={media.src}
            playsInline
            muted={!videoAudioEnabled}
            preload="auto"
            ref={videoRef}
            onEnded={handleVideoEnded}
            onError={(e) => {
              // 動画 URL が読めない場合（404 / CORS / フォーマット不正 等）に
              // 'ended' イベントが発火せず canAdvance が false のまま固まる soft-lock を防ぐ。
              // 進行可能フラグだけ立てて、後続の確認ボタン / タップで前進できる状態に戻す。
              // eslint-disable-next-line no-console
              console.warn(
                '[scene-video] failed to load video, advancing as fallback:',
                e.currentTarget.error
              );
              handleVideoEnded();
            }}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              ...(camera
                ? { transform: camera.transform, transition: camera.transition, transformOrigin: camera.transformOrigin }
                : {})
            }}
          />
        </div>
      )}
      {media?.type === 'color' && (
        <div
          className="layout-renderer__bg"
          style={{
            backgroundColor: media.color ?? '#050915',
            ...(camera
              ? { transform: camera.transform, transition: camera.transition, transformOrigin: camera.transformOrigin }
              : {})
          }}
        />
      )}

      {/* P7-3: 背景クロスディゾルブ。旧背景の src が変わった直後だけ、新背景の上に
          旧背景を重ねて opacity フェードアウトする（video 背景は対象外＝ここに来ない）。
          DOM順で新背景（上の layout-renderer__bg 群）より後＝視覚的に上に重なる。
          ディゾルブ完了後（BG_DISSOLVE_CLEANUP_MS）は outgoingBg が null になり自動的に消える。 */}
      {outgoingBg?.media && (
        <div
          className={`layout-renderer__bg layout-renderer__bg--outgoing${outgoingBgFading ? ' is-fading' : ''}`}
          aria-hidden="true"
        >
          {outgoingBg.media.type === 'image' && (
            <img
              src={outgoingBg.media.src}
              alt=""
              style={
                outgoingBg.camera
                  ? {
                      transform: outgoingBg.camera.transform,
                      transformOrigin: outgoingBg.camera.transformOrigin
                    }
                  : undefined
              }
            />
          )}
          {outgoingBg.media.type === 'color' && (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: outgoingBg.media.color ?? '#050915'
              }}
            />
          )}
        </div>
      )}

      <div className="layout-renderer__overlay">
        <StepContent step={step} waiting={waiting} state={state} camera={camera} canAdvance={canAdvance} onRequestAdvance={onRequestAdvance} showAdvanceHint={tapAdvanceActive} autoAdvanceProgress={autoAdvanceProgress} onEndingAction={onEndingAction} onGenerateCertificate={onGenerateCertificate} onCompleteEnding={onCompleteEnding} playthroughId={playthroughId} scenarioId={scenarioId} onLineDialogChoiceSelect={onLineDialogChoiceSelect} onLineDialogFreeInputSubmit={onLineDialogFreeInputSubmit} onLineDialogComplete={onLineDialogComplete} onDialogRevealControllerChange={handleDialogRevealControllerChange} onDialogRevealDoneChange={onDialogRevealDoneChange} />
      </div>

      {/* P7-3: fade-through veil。step.id 変化の瞬間に薄い暗幕をディップさせ、ハードカットの
          「パワポ送り」感を消す。minigame / video 背景 scene / line_dialog は除外
          （isVeilExemptStep）。pointer-events:none で入力を一切塞がない。 */}
      <div className={`step-transition-veil${veilActive ? ' is-active' : ''}`} aria-hidden="true" />

      {isSceneVideo && (
        <div className="video-tap-layer" onClick={handleTap} role="button" aria-label="video tap layer" />
      )}
      {isSceneVideo && (needsTapToPlay || isVideoEnded) && (
        <div className="video-tap-hint">
          {needsTapToPlay ? 'タップで再生（音声あり）' : '再生完了：タップで次へ'}
        </div>
      )}

      {/* P7-1 a11y代替: 視覚非表示だがフォーカス可能な進行ボタン。タップ送り対象と同じ条件で
          DOM に存在させ、キーボード操作（Tab→Enter/Space）でも進行できるようにする。
          onClick はステージ側の handleStageClick と同じ onRequestAdvance を直接呼ぶ
          （closest('button') ガードの対象自体なのでステージ委譲ではなく直接呼び出しでよい）。
          P7-5: 文字送り中の2段階タップ規則はここにも適用する（キーボード操作でも同じ挙動にする）。 */}
      {tapAdvanceActive && (
        <button
          type="button"
          className="sr-only-advance-btn"
          aria-label="次へ進む"
          onClick={() => {
            if (isDialogStep && dialogRevealControllerRef.current?.isRevealing) {
              dialogRevealControllerRef.current.completeReveal();
              return;
            }
            onRequestAdvance?.();
          }}
        />
      )}
    </div>
  );
}

function StepContent({
  step,
  waiting,
  state,
  camera,
  canAdvance,
  onRequestAdvance,
  showAdvanceHint,
  autoAdvanceProgress,
  onEndingAction,
  onGenerateCertificate,
  onCompleteEnding,
  playthroughId,
  scenarioId,
  onLineDialogChoiceSelect,
  onLineDialogFreeInputSubmit,
  onLineDialogComplete,
  onDialogRevealControllerChange,
  onDialogRevealDoneChange
}: {
  step: StepSchema;
  waiting: WaitingState | null;
  state?: RunState | null;
  camera: CameraTransform | null;
  canAdvance?: boolean;
  onRequestAdvance?: () => void;
  /** P7-1: タップ送りが有効な間、dialog の ▼ 呼吸インジケータを表示するためのフラグ。 */
  showAdvanceHint?: boolean;
  /** P7-1: dialog の自動送りタイマーの進行状況（ヘアライン描画用） */
  autoAdvanceProgress?: AutoAdvanceProgress | null;
  onEndingAction?: (action: 'replay' | 'home' | 'url', url?: string) => void;
  onGenerateCertificate?: () => Promise<ExtendedCertificate>;
  onCompleteEnding?: (endingId?: string) => Promise<void>;
  /** P9-1: 図鑑記録の冪等キー（EndingView へ渡す）。 */
  playthroughId?: string;
  /** 図鑑を現在の物語へ絞るためのシナリオ id。 */
  scenarioId?: string;
  onLineDialogChoiceSelect?: (choiceIndex?: number) => void;
  onLineDialogFreeInputSubmit?: (text: string) => void;
  onLineDialogComplete?: () => void;
  /** P7-5: DialogView の reveal 制御を親（StepLayoutRenderer）の ref へ伝える。 */
  onDialogRevealControllerChange?: (controller: DialogRevealController) => void;
  /** P7-5: 文字送り完了状態の変化を App 層まで伝える（autoAdvance 起算条件用）。 */
  onDialogRevealDoneChange?: (revealDone: boolean) => void;
}): JSX.Element {
  switch (step.type) {
    case 'scene':
      return <SceneView step={step} waiting={waiting} canAdvance={canAdvance} onRequestAdvance={onRequestAdvance} />;
    case 'dialog':
    case 'ai_dialog':
      return (
        <DialogView
          step={step}
          waiting={waiting}
          state={state}
          camera={camera}
          showAdvanceHint={showAdvanceHint}
          autoAdvanceProgress={autoAdvanceProgress}
          onRevealControllerChange={onDialogRevealControllerChange}
          onRevealDoneChange={onDialogRevealDoneChange}
        />
      );
    case 'free_input':
      return <FreeInputView step={step} waiting={waiting} state={state} camera={camera} />;
    case 'spot_gate':
      return <SpotGateView step={step} waiting={waiting} />;
    case 'line_dialog':
      // P7-4: presentation='panel' のときだけ FAB+スライドパネルのシェルで包む。
      // 未指定/'fullscreen' は従来どおり LineDialogView を直接描画（1pxも変えない）。
      return (step as any).presentation === 'panel' ? (
        <LineDialogPanelShell key={step.id} step={step} state={state} onChoiceSelect={onLineDialogChoiceSelect} onFreeInputSubmit={onLineDialogFreeInputSubmit} onComplete={onLineDialogComplete} />
      ) : (
        <LineDialogView key={step.id} step={step} state={state} onChoiceSelect={onLineDialogChoiceSelect} onFreeInputSubmit={onLineDialogFreeInputSubmit} onComplete={onLineDialogComplete} />
      );
    case 'minigame':
      return <MinigameView />;
    case 'branch':
      return <BranchView step={step} waiting={waiting} />;
    case 'reward':
      return <RewardView step={step} waiting={waiting} />;
    case 'ending':
      return <EndingView step={step} runState={state} onAction={onEndingAction} onGenerateCertificate={onGenerateCertificate} onCompleteEnding={onCompleteEnding} playthroughId={playthroughId} scenarioId={scenarioId} />;
    case 'variable_mutation':
      // variable_mutationは自動処理されるため、UIは表示しない
      // 将来的にはここで変数変化の演出（例：「存在度 -1」のポップアップ）を追加可能
      return <></>;
    default:
      return <GenericView step={step as StepSchema} />;
  }
}

function SpotGateView(_props: {
  step: Extract<StepSchema, { type: 'spot_gate' }>;
  waiting: WaitingState | null;
}) {
  return <div className="spot-gate-stage-backdrop" aria-hidden="true" />;
}

function FreeInputView({
  step,
  waiting,
  state,
  camera
}: {
  step: Extract<StepSchema, { type: 'free_input' }>;
  waiting: WaitingState | null;
  state?: RunState | null;
  camera: CameraTransform | null;
}) {
  const layout = resolveLayout(step.layout, 'dialog_overlay');
  const params = layout.templateId === 'dialog_overlay' ? layout.params : defaultLayout('dialog_overlay').params;
  const dialogBox = (params as any).dialogBox ?? {};
  const speakerFocus = (params as any).speakerFocus ?? {};

  const cursor = state ? readFreeInputReplyCursor(state.flags) : null;
  const replyText = cursor && cursor.stepId === step.id ? cursor.lines[cursor.index] : null;

  const speakerName = step.speaker?.name ?? '？？？';
  const speakerColor = step.speaker?.nameColor;

  const scenarioFocusEnabled = state ? readDialogSpeakerFocusScenarioEnabled(state.flags) : true;
  const focusEnabled = typeof speakerFocus.enabled === 'boolean' ? speakerFocus.enabled : scenarioFocusEnabled;
  const zoomScale = typeof speakerFocus.zoomScale === 'number' ? speakerFocus.zoomScale : 1.15;
  const transitionMs = typeof speakerFocus.transitionMs === 'number' ? speakerFocus.transitionMs : 180;
  const inactiveOpacity = typeof speakerFocus.inactiveOpacity === 'number' ? speakerFocus.inactiveOpacity : 0.45;

  const leftSrc = step.portraits?.leftSrc;
  const rightSrc = step.portraits?.rightSrc;
  const leftPos = (step as any).portraitPositions?.left ?? null;
  const rightPos = (step as any).portraitPositions?.right ?? null;

  const activeSide = resolveActivePortraitSideForSingleSpeaker({
    activePortraitSide: (step as any).activePortraitSide,
    speakerKey: (step as any).speakerKey,
    speakerSideMap: (step as any).speakerSideMap,
    leftSrc,
    rightSrc
  });

  return (
    <div className="dialog-view dialog-stage">
      <div
        className="dialog-stage__camera"
        style={camera ? { transform: camera.transform, transition: camera.transition, transformOrigin: camera.transformOrigin } : undefined}
      >
        <Portrait
          position="left"
          src={leftSrc}
          pos={leftPos}
          focus={
            focusEnabled
              ? {
                  opacity: activeSide === 'left' ? 1 : activeSide === 'right' ? inactiveOpacity : inactiveOpacity,
                  scale: activeSide === 'left' ? zoomScale : 1,
                  transitionMs
                }
              : { opacity: 1, scale: 1, transitionMs }
          }
        />
        <Portrait
          position="right"
          src={rightSrc}
          pos={rightPos}
          focus={
            focusEnabled
              ? {
                  opacity: activeSide === 'right' ? 1 : activeSide === 'left' ? inactiveOpacity : inactiveOpacity,
                  scale: activeSide === 'right' ? zoomScale : 1,
                  transitionMs
                }
              : { opacity: 1, scale: 1, transitionMs }
          }
        />
      </div>
      <div
        className="dialog-view__box dialog-stage__box"
        style={{
          height: `${dialogBox.heightPct ?? 35}%`,
          opacity: dialogBox.opacity ?? 0.92
        }}
      >
        <p className="dialog-view__speaker" style={{ color: speakerColor ?? '#fef3c7' }}>
          {speakerName || '？？？'}
        </p>
        <p className="dialog-view__text">
          {replyText ??
            (waiting && waiting.type === 'free_input'
              ? '（入力待ち）'
              : step.promptText ?? '（入力）')}
        </p>
      </div>
    </div>
  );
}

function SceneView({
  step,
  waiting,
  canAdvance,
  onRequestAdvance
}: {
  step: Extract<StepSchema, { type: 'scene' }>;
  waiting: WaitingState | null;
  canAdvance?: boolean;
  onRequestAdvance?: () => void;
}) {
  // 技術的なノート（[BG画像:...] など）はユーザーに表示しない
  const isDebugNote = step.notes && /^\[(?:BG|DEBUG|TODO|NOTE)/i.test(step.notes.trim());
  const displayHint = !isDebugNote && step.notes ? step.notes : null;
  const sceneConfig = (step as any).config as
    | {
        imageOnly?: boolean;
        confirmButton?: { label?: string; position?: 'top' | 'bottom' };
      }
    | undefined;
  const imageOnly = sceneConfig?.imageOnly === true;
  const confirmButton = sceneConfig?.confirmButton;
  const confirmPosition = confirmButton?.position === 'bottom' ? 'bottom' : 'top';

  return (
    <div className={`scene-view${imageOnly ? ' scene-view--image-only' : ''}`}>
      {confirmButton && canAdvance && (
        <button
          type="button"
          className={`scene-confirm-btn scene-confirm-btn--${confirmPosition}`}
          onClick={onRequestAdvance}
        >
          {confirmButton.label ?? '確認して次へ'}
        </button>
      )}
      {!imageOnly && (
        <>
          {step.title && <h2>{step.title}</h2>}
          {displayHint && <p className="scene-view__hint">{displayHint}</p>}
        </>
      )}
      {waiting && <WaitingBadge waiting={waiting} />}
    </div>
  );
}

/** P7-2: dialog ウインドウの「顔」。line 指定 > step 指定 > 既定'subtitle' の優先順で解決する。 */
type DialogFace = 'subtitle' | 'antique';

function resolveDialogFace(activeLine: any, step: Extract<StepSchema, { type: 'dialog' | 'ai_dialog' }>): DialogFace {
  const lineFace = activeLine && typeof activeLine.face === 'string' ? activeLine.face : undefined;
  if (lineFace === 'subtitle' || lineFace === 'antique') return lineFace;
  const stepFace = (step as any).face;
  if (stepFace === 'subtitle' || stepFace === 'antique') return stepFace;
  return 'subtitle';
}

/** P7-2: face 切替のコンテナごとクロスフェード時間（ms）。shell-tokens.css の --shell-dur-face-swap（旧 --shell-face-swap-ms）と一致させる。 */
const FACE_SWAP_MS = 250;
/** P7-2: 同一 face 内で行だけ変わる時のフェード時間（ms）。--shell-dur-face-line-fade（旧 --shell-face-line-fade-ms）と一致させる。 */
const FACE_LINE_FADE_MS = 180;

/** dialog の1行分、face切替クロスフェードに乗せる表示内容。 */
interface DialogFaceContent {
  face: DialogFace;
  speakerName: string;
  speakerColor?: string;
  text?: string;
}

/**
 * P7-2: dialog ウインドウの「顔」切替をコンテナごとクロスフェードで表示するためのフック。
 *
 * content ごと displayed state に保持するのは、フェードアウト中（visible=false の間）に
 * 表示され続ける中身が「新しい行のテキスト」に先行して書き変わってしまうと、フェードで
 * 隠しているはずの一瞬に新テキストがちらつく可能性があるため（React は再レンダーのたびに
 * 最新 props を使うので、テキストだけ親から直接渡すと防げない）。
 *
 * - key が変わらない（同一 face 内で行だけ進む）: 短いフェード（FACE_LINE_FADE_MS）で中身を差し替える。
 * - face 自体が変わる: 長め（FACE_SWAP_MS）でフェードアウト→中身を差し替え→フェードインする
 *   （枠の出現自体が「異質な割り込み」の演出になるため、字幕↔骨董の切替だけ長く取る）。
 * - 連打セーフ: 呼び出しごとに世代トークン（seq）を発行し、フェード待ち中に新しい face/key が
 *   来たら古いタイマーの反映を無視する（必ず最後の状態が勝つ。表示が壊れない）。
 * - reduced-motion: prefers-reduced-motion 時は待たずに即時反映する。
 */
function useFaceCrossfade(key: string, content: DialogFaceContent) {
  const [displayed, setDisplayed] = useState<{ key: string; content: DialogFaceContent; visible: boolean }>({
    key,
    content,
    visible: true
  });
  const seqRef = useRef(0);
  const prevKeyRef = useRef(key);
  const prevFaceRef = useRef(content.face);

  useEffect(() => {
    if (prevKeyRef.current === key) {
      return;
    }
    const faceChanged = prevFaceRef.current !== content.face;
    prevKeyRef.current = key;
    prevFaceRef.current = content.face;

    const reducedMotion =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const mySeq = ++seqRef.current;
    if (reducedMotion) {
      setDisplayed({ key, content, visible: true });
      return;
    }

    // 1) フェードアウト（visible=false・中身はまだ古いまま＝ちらつき防止）
    setDisplayed((prev) => ({ ...prev, visible: false }));
    const waitMs = faceChanged ? FACE_SWAP_MS : FACE_LINE_FADE_MS;
    const timer = window.setTimeout(() => {
      if (seqRef.current !== mySeq) return; // 連打で追い越された：この反映は捨てる
      // 2) 中身を差し替えてフェードイン
      setDisplayed({ key, content, visible: true });
    }, waitMs);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return displayed;
}

/**
 * P7-5: dialog/ai_dialog の文字送り（typewriter reveal）。
 *
 * - `revealKey` が変わるたびに 0 文字目からタイマーで 1 文字ずつ表示していく
 *   （TYPEWRITER_MS_PER_CHAR ms/文字）。サロゲートペア・絵文字で文字化けしないよう、
 *   文字列は Array.from でコードポイント単位に分割する（.length や [i] は使わない）。
 * - `completeReveal()` を呼ぶと即座に全文表示に切り替え、残りのタイマーを止める
 *   （表示中タップ→全文即表示、の1段目に対応）。
 * - reduced-motion では常に全文即表示（従来挙動）。
 * - effect の依存配列は `revealKey` のみにしている（chars/reducedMotion は ref 経由で読む）。
 *   fullText から作る配列は毎レンダー新しい参照になるため依存に含めると、revealKey が
 *   変わっていないのに親の再レンダー（onRevealDoneChange 経由の App 側 setState 等）で
 *   effect が再実行され、setInterval が張り直されずに文字送りが永久に止まる不具合が
 *   実機検証で見つかった（修正済み）。
 */
function useTypewriterReveal(revealKey: string, fullText: string) {
  const reducedMotion =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  const [revealedCount, setRevealedCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof window.setInterval> | null>(null);
  // 直近の chars/reducedMotion を ref に保持し、effect の依存配列は revealKey のみにする。
  // 理由: chars は fullText から都度 Array.from される新しい配列参照になるため、依存に含めると
  // 「文字列は変わっていないのに親の再レンダー（例: onRevealDoneChange 経由の App 側 setState）
  // で effect が再実行され、setInterval がクリアされた直後に “key不変だから何もしない” 分岐で
  // 再アームされずタイマーが永久に止まる」事故を起こす（実機検証で発見・修正済み）。
  const charsRef = useRef<string[]>([]);
  const reducedMotionRef = useRef(reducedMotion);
  charsRef.current = useMemo(() => Array.from(fullText ?? ''), [fullText]);
  reducedMotionRef.current = reducedMotion;

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const chars = charsRef.current;
    if (reducedMotionRef.current || chars.length === 0) {
      setRevealedCount(chars.length);
      return undefined;
    }

    setRevealedCount(0);
    let count = 0;
    timerRef.current = window.setInterval(() => {
      count += 1;
      if (count >= chars.length) {
        setRevealedCount(chars.length);
        if (timerRef.current !== null) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        return;
      }
      setRevealedCount(count);
    }, TYPEWRITER_MS_PER_CHAR);

    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    // revealKey が変わった時（＝新しい行になった時）にだけ 0 文字目から再アームする。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealKey]);

  const completeReveal = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRevealedCount(charsRef.current.length);
  };

  const chars = charsRef.current;
  const isRevealing = revealedCount < chars.length;
  const displayedText = chars.slice(0, revealedCount).join('');

  return { displayedText, isRevealing, completeReveal };
}

function DialogView({
  step,
  waiting,
  state,
  camera,
  showAdvanceHint,
  autoAdvanceProgress,
  onRevealControllerChange,
  onRevealDoneChange
}: {
  step: Extract<StepSchema, { type: 'dialog' | 'ai_dialog' }>;
  waiting: WaitingState | null;
  state?: RunState | null;
  camera: CameraTransform | null;
  /** P7-1: 「まだ続きがある」の控えめな合図（▼呼吸アニメ）を出すかどうか。 */
  showAdvanceHint?: boolean;
  /** P7-1: 自動送りタイマーの進行状況（アーム中のみ）。box 上辺のヘアラインに反映する。 */
  autoAdvanceProgress?: AutoAdvanceProgress | null;
  /** P7-5: 文字送りの reveal 状態が変わるたびに呼ばれる。親（StepLayoutRenderer）が
   *  handleStageClick のタップ分岐と、App 層への revealDone 通知に使う。 */
  onRevealControllerChange?: (controller: DialogRevealController) => void;
  /** P7-5: 文字送り完了状態（revealDone = !isRevealing）が変わるたびに呼ばれる。
   *  App 層の autoAdvance 起算条件に使う。setState を伴うため useEffect 経由で呼ぶ
   *  （レンダー中に呼ぶと親の state 更新を子のレンダー中に誘発してしまうため）。 */
  onRevealDoneChange?: (revealDone: boolean) => void;
}) {
  const layout = resolveLayout(step.layout, 'dialog_overlay');
  const params = layout.templateId === 'dialog_overlay' ? layout.params : defaultLayout('dialog_overlay').params;
  const dialogBox = (params as any).dialogBox ?? {};
  const speakerFocus = (params as any).speakerFocus ?? {};
  const dialog = step.type === 'dialog' ? (step as any) : null;
  const cursor = dialog && state ? readDialogCursor(state.flags) : null;
  const lines = dialog && Array.isArray(dialog.lines) ? (dialog.lines as any[]) : null;
  const lineIndex = dialog && lines && lines.length > 0 ? (cursor && cursor.stepId === dialog.id ? cursor.index : 0) : 0;
  const activeLine = dialog && lines && lines.length > 0 ? lines[Math.min(Math.max(0, lineIndex), lines.length - 1)] : null;
  // P8-3: ai_dialog の英語固定ラベル 'AI Dialog' を廃止。名前を持たない声は
  // 既存フォールバックの「？？？」に倒す（未知の話者＝ミステリの語彙・guide §4.6）。
  const speakerName = step.type === 'dialog' ? (activeLine?.speaker?.name ?? step.speaker?.name) : undefined;
  const speakerColor = step.type === 'dialog' ? (activeLine?.speaker?.nameColor ?? step.speaker?.nameColor) : undefined;
  const dialogText = step.type === 'dialog' ? (activeLine?.text ?? step.text) : step.userPrompt;
  const face = resolveDialogFace(activeLine, step);
  // P7-2: 表示中の行を一意に識別するキー（stepId:lineIndex）。face 切替クロスフェードの世代判定に使う。
  const lineKey = `${step.id}:${lineIndex}`;
  const facePresentation = useFaceCrossfade(lineKey, {
    face,
    speakerName: speakerName || '？？？',
    speakerColor,
    text: dialogText
  });
  // P7-5: 文字送りは「実際に画面へ表示される行」（facePresentation.content.text）をキーにする。
  // face クロスフェード中は中身がまだ古いテキストのまま（ちらつき防止）なので、キーも
  // facePresentation.key を使い、フェード完了で中身が差し替わったタイミングで再アームする。
  const revealKey = `${facePresentation.key}:${facePresentation.content.text ?? ''}`;
  const { displayedText, isRevealing, completeReveal } = useTypewriterReveal(
    revealKey,
    facePresentation.content.text ?? ''
  );
  // レンダーのたびに最新の reveal 制御を親へ伝える（タップ時の分岐に使う ref 更新パターン）。
  // useEffect ではなくレンダー中の呼び出しにすると親の setState を子のレンダー中に誘発しかねないため、
  // 親側は ref を直接更新するだけの副作用フリーな関数を渡す前提にする（onRevealControllerChange 参照）。
  onRevealControllerChange?.({ isRevealing, completeReveal });
  // P7-5: revealDone（=!isRevealing）の変化だけを App 層へ伝える。setState を伴う可能性がある
  // ため、レンダー中でなく effect で通知する（App.tsx の autoAdvance 起算条件用）。
  useEffect(() => {
    onRevealDoneChange?.(!isRevealing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealing]);
  const effectivePortraits = (() => {
    if (step.type !== 'dialog') return { leftSrc: undefined, rightSrc: undefined };
    let left = step.portraits?.leftSrc;
    let right = step.portraits?.rightSrc;
    let hideLeft = false;
    let hideRight = false;
    if (lines && lines.length > 0) {
      const max = Math.min(lineIndex, lines.length - 1);
      for (let i = 0; i <= max; i += 1) {
        const ln = lines[i];
        if (ln?.resetPortraits) {
          left = step.portraits?.leftSrc;
          right = step.portraits?.rightSrc;
          hideLeft = false;
          hideRight = false;
        }
        if (ln?.portraits) {
          if (typeof ln.portraits.leftSrc === 'string') left = ln.portraits.leftSrc;
          if (typeof ln.portraits.rightSrc === 'string') right = ln.portraits.rightSrc;
          if (typeof ln.portraits.hideLeft === 'boolean') hideLeft = ln.portraits.hideLeft;
          if (typeof ln.portraits.hideRight === 'boolean') hideRight = ln.portraits.hideRight;
        }
      }
    }
    return { leftSrc: hideLeft ? undefined : left, rightSrc: hideRight ? undefined : right };
  })();
  const leftSrc = step.type === 'dialog' ? effectivePortraits.leftSrc : undefined;
  const rightSrc = step.type === 'dialog' ? effectivePortraits.rightSrc : undefined;
  const leftPos = step.type === 'dialog' ? (step as any).portraitPositions?.left : null;
  const rightPos = step.type === 'dialog' ? (step as any).portraitPositions?.right : null;
  // NOTE: 背景は buildBackgroundStyle(step, state) 側で lines.bg を参照している

  const scenarioFocusEnabled = state ? readDialogSpeakerFocusScenarioEnabled(state.flags) : true;
  const focusEnabled =
    typeof speakerFocus.enabled === 'boolean' ? speakerFocus.enabled : scenarioFocusEnabled;
  const zoomScale = typeof speakerFocus.zoomScale === 'number' ? speakerFocus.zoomScale : 1.15;
  const transitionMs = typeof speakerFocus.transitionMs === 'number' ? speakerFocus.transitionMs : 180;
  const inactiveOpacity =
    typeof speakerFocus.inactiveOpacity === 'number' ? speakerFocus.inactiveOpacity : 0.45;

  const activeSide =
    step.type === 'dialog'
      ? resolveActivePortraitSide({
          step: step as any,
          lines,
          lineIndex,
          leftSrc,
          rightSrc
        })
      : null;
  // 字幕faceの汎用すそ消しは、焼き込み文字を隠すための互換処理。
  // 正式な全身立ち絵は step.portraitBottomFade=false でオプトアウトし、靴まで不透明に表示できる。
  const shouldBottomFade =
    facePresentation.content.face === 'subtitle' && (step as any).portraitBottomFade !== false;

  return (
    <div className="dialog-view dialog-stage">
      <div
        className="dialog-stage__camera"
        style={camera ? { transform: camera.transform, transition: camera.transition, transformOrigin: camera.transformOrigin } : undefined}
      >
        <Portrait
          position="left"
          src={leftSrc}
          pos={leftPos}
          bottomFade={shouldBottomFade}
          focus={
            focusEnabled
              ? {
                  opacity:
                    activeSide === 'left' ? 1 : activeSide === 'right' ? inactiveOpacity : inactiveOpacity,
                  scale: activeSide === 'left' ? zoomScale : 1,
                  transitionMs
                }
              : { opacity: 1, scale: 1, transitionMs }
          }
        />
        <Portrait
          position="right"
          src={rightSrc}
          pos={rightPos}
          bottomFade={shouldBottomFade}
          focus={
            focusEnabled
              ? {
                  opacity:
                    activeSide === 'right' ? 1 : activeSide === 'left' ? inactiveOpacity : inactiveOpacity,
                  scale: activeSide === 'right' ? zoomScale : 1,
                  transitionMs
                }
              : { opacity: 1, scale: 1, transitionMs }
          }
        />
      </div>
      <div
        className={`dialog-face-shell dialog-stage__box dialog-face-${facePresentation.content.face}`}
        style={{
          opacity: facePresentation.visible ? 1 : 0
          // P7-2修正②: dialogBox の高さ系パラメータ（heightPct/opacity/paddingPx/radiusPx/blur）は
          // 字幕/骨董どちらの face でも適用しない。字幕は枠が無く意味を保てない、骨董はテキスト量に
          // 応じた自然な高さ（CSS の min-height + padding）が正。旧 box 挙動（heightPct 等の適用）は
          // FreeInputView（.dialog-view__box）にのみ残存する。showSpeakerName は引き続き尊重（下記）。
        }}
      >
        {/* P7-1: 自動送りヘアライン。face により配置（字幕=テキスト塊上辺／骨董=box上辺）が
            style.css 側の .dialog-face-{subtitle,antique} .dialog-view__auto-advance-hairline で切り替わる。
            key に armKey（stepId:lineIndex）を使い、行が変わるたびに DOM ノードを作り直すことで
            CSS アニメーションを確実にリセットする（同一 key のままだと transition が再発火しない）。
            P7-5: autoAdvanceProgress は App 層で「文字送り完了後」にしかアームされない
            （isRevealing を armKey 決定に含める＝下記 App.tsx 側）。ここでは念のため isRevealing
            中は描画しない防御も入れておく（表示中に古いヘアラインが一瞬見えるのを防ぐ）。 */}
        {autoAdvanceProgress && !isRevealing && (
          <span
            key={autoAdvanceProgress.armKey}
            className="dialog-view__auto-advance-hairline"
            aria-hidden="true"
            style={{ animationDuration: `${autoAdvanceProgress.delayMs}ms` }}
          />
        )}
        {/* dialogBox.showSpeakerName（既定true）は両faceで意味を保てるため尊重する。
            骨董faceは話者名が空のとき「◆」だけが浮かないよう要素自体を出さない（世界観 §7.4: 台詞や意思を持たせない）。 */}
        {dialogBox.showSpeakerName !== false &&
          (facePresentation.content.face === 'antique' ? Boolean(facePresentation.content.speakerName) : true) && (
            <p
              className="dialog-view__speaker"
              style={facePresentation.content.speakerColor ? { color: facePresentation.content.speakerColor } : undefined}
            >
              {facePresentation.content.speakerName}
            </p>
          )}
        {/* P7-5: 全文でなく文字送りの displayedText を描画する。reduced-motion では
            useTypewriterReveal が初期状態から全文を返すため、実質従来どおり一括表示になる。 */}
        <p className="dialog-view__text">{displayedText}</p>
        {/* P7-5: ▼ は全文表示が完了してから（表示中はまだ続きがあることを ▼ で二重に示す必要がない・
            むしろ「タップで先読みできる」ことを誤読させないため、reveal 完了後にのみ出す）。 */}
        {showAdvanceHint && !waiting && !isRevealing && (
          <span className="dialog-view__advance-hint" aria-hidden="true">
            ▼
          </span>
        )}
      </div>
      {waiting && <WaitingBadge waiting={waiting} />}
    </div>
  );
}

function Portrait({
  position,
  src,
  pos,
  focus,
  bottomFade
}: {
  position: 'left' | 'right';
  src?: string;
  pos?: {
    xPct?: number;
    yPct?: number;
    scale?: number;
    widthPct?: number;
    heightPct?: number;
    fit?: 'cover' | 'contain' | 'contain_landscape';
    cropXPct?: number;
    cropYPct?: number;
  } | null;
  focus?: { opacity: number; scale: number; transitionMs: number };
  /** P7-2修正①: 字幕face（枠なし）のとき true。立ち絵の下部をフェードアウト（すそ消し）して
   *  旧boxが覆い隠していた立ち絵下端（プレースホルダ絵柄に焼き込まれた名前等）を背景に溶かし、
   *  字幕の話者名と二重に見える問題を防ぐ。骨董face / FreeInputView では従来どおり非適用。 */
  bottomFade?: boolean;
}) {
  const resolved = typeof src === 'string' ? (resolveMediaRefToUrl(src) ?? src) : undefined;
  if (!resolved) {
    return null;
  }
  const x = typeof pos?.xPct === 'number' ? pos.xPct : position === 'left' ? 25 : 75;
  const y = typeof pos?.yPct === 'number' ? pos.yPct : 55;
  const scale = typeof pos?.scale === 'number' ? pos.scale : 1;
  const baseW = typeof pos?.widthPct === 'number' ? pos.widthPct : 40;
  const baseH = typeof pos?.heightPct === 'number' ? pos.heightPct : 60;
  const w = Math.min(100, Math.max(1, baseW * scale));
  const h = Math.min(100, Math.max(1, baseH * scale));
  const cropX = typeof pos?.cropXPct === 'number' ? pos.cropXPct : 50;
  const cropY = typeof pos?.cropYPct === 'number' ? pos.cropYPct : 35;
  const fit = pos?.fit ?? 'cover';
  const transitionMs = typeof focus?.transitionMs === 'number' ? focus.transitionMs : 180;
  // backOut / backIn 系の easing（y>1 / y<0）で「ズームイン→ちょい行き過ぎ→戻る / ズームアウト→ちょい引く→戻る」を作る
  // P8-2c: 重複リテラルを utils/motion.ts の同値定数へ一元化（= --shell-ease-emphasis / --shell-ease-back-in）
  const transformEasing =
    typeof focus?.scale === 'number' && focus.scale > 1 ? EASE_EMPHASIS : EASE_BACK_IN;
  return (
    <img
      className={`dialog-stage__portrait ${position} dialog-stage__portrait--fit-${fit.replace('_', '-')}${bottomFade ? ' dialog-stage__portrait--bottom-fade' : ''}`}
      src={resolved}
      alt={`${position} portrait`}
      style={{
        opacity: typeof focus?.opacity === 'number' ? focus.opacity : 1,
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        width: `${w}%`,
        height: `${h}%`,
        transform: `translate(-50%, -50%) scale(${typeof focus?.scale === 'number' ? focus.scale : 1})`,
        transition: `transform ${transitionMs}ms ${transformEasing}, opacity ${transitionMs}ms ease-out`,
        objectPosition: `${cropX}% ${cropY}%`,
        // テストプレイで「謎の枠」に見える要因を排除（背景に存在しているように見せる）
        border: 'none',
        background: 'transparent',
        borderRadius: 0,
        display: 'block'
      }}
    />
  );
}

function MinigameView() {
  // Playerのステージ上に直接レンダリングするため、ここでは表示しない
  return null;
}

function BranchView(_props: {
  step: Extract<StepSchema, { type: 'branch' }>;
  waiting: WaitingState | null;
}) {
  // StepInteractionOverlay がインタラクティブな選択肢を表示するため、
  // ここでは何も表示しない（重複表示を防ぐ）
  return null;
}

function RewardView({
  step,
  waiting
}: {
  step: Extract<StepSchema, { type: 'reward' }>;
  waiting: WaitingState | null;
}) {
  return (
    <div className="layout-panel reward">
      <p className="layout-panel__title">{step.title || '成果'}</p>
      <p className="layout-panel__meta">結果: {step.resultType}</p>
      <p className="layout-panel__meta">次の挙動: {step.nextAction}</p>
      {step.stats && (
        <ul className="reward-stats">
          {typeof step.stats.accuracy === 'number' && (
            <li>
              <span>正確さ</span>
              <strong>{step.stats.accuracy}%</strong>
            </li>
          )}
          {typeof step.stats.hints === 'number' && (
            <li>
              <span>ヒント</span>
              <strong>{step.stats.hints}</strong>
            </li>
          )}
          {typeof step.stats.mistakes === 'number' && (
            <li>
              <span>ミス</span>
              <strong>{step.stats.mistakes}</strong>
            </li>
          )}
        </ul>
      )}
      {waiting && <WaitingBadge waiting={waiting} />}
    </div>
  );
}

function GenericView({ step }: { step: StepSchema }) {
  return (
    <div className="layout-panel generic">
      <p className="layout-panel__title">{step.title || step.id}</p>
      <pre className="layout-panel__json">{JSON.stringify(step, null, 2)}</pre>
    </div>
  );
}

function WaitingBadge({ waiting }: { waiting: WaitingState }) {
  // 最終監査 §2-1-4: 本番モードでは内部 enum（waiting.type）を DOM に残さない。
  // 従来の CSS 非表示（.player-shell--production .waiting-badge）は視覚のみの隠蔽で、
  // DOM 検査には内部語彙が漏れていた。dev では従来どおり描画する。
  const productionMode = useProductionMode();
  if (productionMode) {
    return null;
  }
  return <span className="waiting-badge">Waiting: {waiting.type}</span>;
}

function buildBackgroundStyle(step: StepSchema, state?: RunState | null): React.CSSProperties {
  if (step.type === 'scene' && step.bg.kind === 'image' && step.bg.src) {
    const url = resolveMediaRefToUrl(step.bg.src) ?? step.bg.src;
    return {
      backgroundColor: '#020617',
      backgroundImage: `linear-gradient(180deg, rgba(2,6,23,0.4), rgba(2,6,23,0.9)), url(${url})`,
      backgroundSize: step.bg.fit === 'contain' ? 'contain' : 'cover',
      backgroundPosition: 'center'
    };
  }
  if (step.type === 'scene' && step.bg.kind === 'color') {
    return { backgroundColor: step.bg.color ?? '#020617' };
  }
  if (step.type === 'minigame') {
    const bg = (step.config as any)?.background as { type?: 'color' | 'image' | 'video'; value?: string; overlay?: string } | undefined;
    if (bg?.type === 'image' && bg.value) {
      const overlay = bg.overlay ? bg.overlay : 'rgba(2,6,23,0.45)';
      return {
        backgroundColor: '#020617',
        backgroundImage: `linear-gradient(180deg, ${overlay}, rgba(2,6,23,0.9)), url(${bg.value})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      };
    }
    if (bg?.type === 'color' && bg.value) {
      return { backgroundColor: bg.value };
    }
    // video はMVPでは背景色にフォールバック（必要なら後続で videoタグ対応）
    if (bg?.type === 'video' && bg.value) {
      return { backgroundColor: '#020617' };
    }
  }
  if (step.type === 'dialog') {
    const cursor = state ? readDialogCursor(state.flags) : null;
    const lines = Array.isArray((step as any).lines) ? ((step as any).lines as any[]) : null;
    const lineIndex = lines && lines.length > 0 ? (cursor && cursor.stepId === step.id ? cursor.index : 0) : 0;
    const activeLine = lines && lines.length > 0 ? lines[Math.min(Math.max(0, lineIndex), lines.length - 1)] : null;
    const bg =
      (activeLine?.bg as { kind?: 'image' | 'color'; src?: string; color?: string; fit?: string; dimOpacity?: number } | undefined) ??
      ((step as any).bg as { kind?: 'image' | 'color'; src?: string; color?: string; fit?: string; dimOpacity?: number } | undefined);
    if (bg?.kind === 'image' && bg.src) {
      const url = resolveMediaRefToUrl(bg.src) ?? bg.src;
      const dim = typeof bg.dimOpacity === 'number' ? bg.dimOpacity : 0.3;
      return {
        backgroundImage: `linear-gradient(180deg, rgba(2,6,23,${Math.min(0.9, dim)}), rgba(2,6,23,0.9)), url(${url})`,
        backgroundSize: bg.fit === 'contain' ? 'contain' : 'cover',
        backgroundPosition: 'center'
      };
    }
    if (bg?.kind === 'color') {
      return { backgroundColor: bg.color ?? '#020617' };
    }
  }
  /* P8-2c: bg 未指定 step（ending 等）の最終フォールバックはステージの地（骨董黒茶）に追従させる。
     旧 #020617 固定だと 01-app-shell の --shell-stage-bg 化（公認の slate→骨董）をインラインで
     上書きしてしまい、ending 外周だけ navy に残る。データ駆動の bg.color ?? フォールバック群は
     シナリオ互換のため据置（allowlist 済）。 */
  return { backgroundColor: 'var(--shell-stage-bg, #120c08)' };
}

function extractBackgroundMedia(
  step: StepSchema,
  state?: RunState | null
): { type: 'image'; src: string } | { type: 'video'; src: string } | { type: 'color'; color?: string } | null {
  if (step.type === 'scene') {
    if (step.bg.kind === 'image' && step.bg.src) {
      const url = resolveMediaRefToUrl(step.bg.src) ?? step.bg.src;
      return { type: 'image', src: url };
    }
    if (step.bg.kind === 'video' && step.bg.src) {
      const url = resolveMediaRefToUrl(step.bg.src) ?? step.bg.src;
      return { type: 'video', src: url };
    }
    if (step.bg.kind === 'color') {
      return { type: 'color', color: step.bg.color };
    }
  }
  if (step.type === 'minigame') {
    const bg = (step.config as any)?.background as { type?: 'color' | 'image' | 'video'; value?: string } | undefined;
    if (bg?.type === 'image' && bg.value) {
      const url = resolveMediaRefToUrl(bg.value) ?? bg.value;
      return { type: 'image', src: url };
    }
    if (bg?.type === 'color' && bg.value) {
      return { type: 'color', color: bg.value };
    }
  }
  if (step.type === 'dialog') {
    // マルチライン対話の背景切替に対応：現在表示中のラインの bg を優先し、なければ step 全体の bg を使う。
    // portraits が設定されている dialog でも背景の <img> / <video> 要素がレンダリングされるよう、
    // buildBackgroundStyle と同じ解決ロジックをここにも適用する。
    const cursor = state ? readDialogCursor(state.flags) : null;
    const lines = Array.isArray((step as any).lines) ? ((step as any).lines as any[]) : null;
    const lineIndex =
      lines && lines.length > 0 ? (cursor && cursor.stepId === step.id ? cursor.index : 0) : 0;
    const activeLine =
      lines && lines.length > 0 ? lines[Math.min(Math.max(0, lineIndex), lines.length - 1)] : null;
    const bg =
      (activeLine?.bg as { kind?: 'image' | 'color' | 'video'; src?: string; color?: string } | undefined) ??
      ((step as any).bg as { kind?: 'image' | 'color' | 'video'; src?: string; color?: string } | undefined);
    if (bg?.kind === 'image' && bg.src) {
      const url = resolveMediaRefToUrl(bg.src) ?? bg.src;
      return { type: 'image', src: url };
    }
    if (bg?.kind === 'video' && bg.src) {
      const url = resolveMediaRefToUrl(bg.src) ?? bg.src;
      return { type: 'video', src: url };
    }
    if (bg?.kind === 'color') {
      return { type: 'color', color: bg.color };
    }
  }
  return null;
}

function resolveLayout(layout: LayoutSchemaV1 | undefined, fallback: LayoutTemplateId): LayoutSchemaV1 {
  if (layout) {
    return layout;
  }
  return defaultLayout(fallback);
}

type DialogCursor = { stepId: string; index: number };
const DIALOG_CURSOR_FLAG_KEY = '__dialogCursor';
function readDialogCursor(flags: Record<string, unknown>): DialogCursor | null {
  const raw = (flags as any)?.[DIALOG_CURSOR_FLAG_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as any;
  if (typeof r.stepId !== 'string' || !Number.isFinite(r.index)) return null;
  return { stepId: r.stepId, index: Number(r.index) };
}

const DIALOG_SPEAKER_FOCUS_FLAG_KEY = '__dialogSpeakerFocusEnabled';
function readDialogSpeakerFocusScenarioEnabled(flags: Record<string, unknown>): boolean {
  const raw = (flags as any)?.[DIALOG_SPEAKER_FOCUS_FLAG_KEY];
  return typeof raw === 'boolean' ? raw : true;
}

type FreeInputReplyCursor = { stepId: string; index: number; lines: string[] };
const FREE_INPUT_REPLY_CURSOR_FLAG_KEY = '__freeInputReplyCursor';
function readFreeInputReplyCursor(flags: Record<string, unknown>): FreeInputReplyCursor | null {
  const raw = (flags as any)?.[FREE_INPUT_REPLY_CURSOR_FLAG_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as any;
  if (typeof r.stepId !== 'string' || !Number.isFinite(r.index) || !Array.isArray(r.lines)) return null;
  const lines = r.lines.filter((x: unknown) => typeof x === 'string') as string[];
  if (lines.length === 0) return null;
  return { stepId: r.stepId, index: Math.max(0, Math.min(lines.length - 1, Number(r.index))), lines };
}

function resolveActivePortraitSideForSingleSpeaker({
  activePortraitSide,
  speakerKey,
  speakerSideMap,
  leftSrc,
  rightSrc
}: {
  activePortraitSide?: unknown;
  speakerKey?: unknown;
  speakerSideMap?: Record<string, unknown> | null;
  leftSrc?: string;
  rightSrc?: string;
}): 'left' | 'right' | 'none' {
  if (activePortraitSide === 'left' || activePortraitSide === 'right' || activePortraitSide === 'none') {
    return activePortraitSide;
  }
  if (typeof speakerKey === 'string' && speakerSideMap && typeof speakerSideMap === 'object') {
    const mapped = (speakerSideMap as any)[speakerKey];
    if (mapped === 'left' || mapped === 'right') return mapped;
  }
  if (leftSrc && !rightSrc) return 'left';
  if (!leftSrc && rightSrc) return 'right';
  return 'none';
}

type CameraTransform = {
  transform: string;
  transition: string;
  transformOrigin: string;
};

function computeCameraTransform(step: StepSchema, state?: RunState | null): CameraTransform | null {
  if (step.type !== 'dialog' && step.type !== 'free_input') {
    return null;
  }

  const layout = resolveLayout((step as any).layout, 'dialog_overlay');
  const params = layout.templateId === 'dialog_overlay' ? (layout as any).params : (defaultLayout('dialog_overlay') as any).params;
  const speakerFocus = params?.speakerFocus ?? {};

  const scenarioFocusEnabled = state ? readDialogSpeakerFocusScenarioEnabled(state.flags) : true;
  const focusEnabled = typeof speakerFocus.enabled === 'boolean' ? speakerFocus.enabled : scenarioFocusEnabled;
  if (!focusEnabled) {
    return { transform: 'translate3d(0,0,0) scale(1)', transition: 'transform 0ms linear', transformOrigin: '50% 50%' };
  }

  const zoomScale = typeof speakerFocus.zoomScale === 'number' ? speakerFocus.zoomScale : 1.15;
  const transitionMs = typeof speakerFocus.transitionMs === 'number' ? speakerFocus.transitionMs : 180;

  const activeSide = (() => {
    if (step.type === 'free_input') {
      return resolveActivePortraitSideForSingleSpeaker({
        activePortraitSide: (step as any).activePortraitSide,
        speakerKey: (step as any).speakerKey,
        speakerSideMap: (step as any).speakerSideMap,
        leftSrc: (step as any).portraits?.leftSrc,
        rightSrc: (step as any).portraits?.rightSrc
      });
    }

    const dialog = step as any;
    const cursor = state ? readDialogCursor(state.flags) : null;
    const lines = Array.isArray(dialog.lines) ? (dialog.lines as any[]) : null;
    const lineIndex = lines && lines.length > 0 ? (cursor && cursor.stepId === dialog.id ? cursor.index : 0) : 0;

    const effectivePortraits = (() => {
      let left = dialog.portraits?.leftSrc;
      let right = dialog.portraits?.rightSrc;
      let hideLeft = false;
      let hideRight = false;
      if (lines && lines.length > 0) {
        const max = Math.min(lineIndex, lines.length - 1);
        for (let i = 0; i <= max; i += 1) {
          const ln = lines[i];
          if (ln?.resetPortraits) {
            left = dialog.portraits?.leftSrc;
            right = dialog.portraits?.rightSrc;
            hideLeft = false;
            hideRight = false;
          }
          if (ln?.portraits) {
            if (typeof ln.portraits.leftSrc === 'string') left = ln.portraits.leftSrc;
            if (typeof ln.portraits.rightSrc === 'string') right = ln.portraits.rightSrc;
            if (typeof ln.portraits.hideLeft === 'boolean') hideLeft = ln.portraits.hideLeft;
            if (typeof ln.portraits.hideRight === 'boolean') hideRight = ln.portraits.hideRight;
          }
        }
      }
      return { leftSrc: hideLeft ? undefined : left, rightSrc: hideRight ? undefined : right };
    })();

    return resolveActivePortraitSide({
      step: dialog,
      lines,
      lineIndex,
      leftSrc: effectivePortraits.leftSrc,
      rightSrc: effectivePortraits.rightSrc
    });
  })();

  // 背景は立ち絵より弱めに動かす（カメラ感）
  const cameraScale = activeSide === 'none' ? 1 : 1 + (zoomScale - 1) * 0.6;
  const offsetXPct = activeSide === 'left' ? 6 : activeSide === 'right' ? -6 : 0;

  // P8-2c: 重複リテラルを utils/motion.ts の同値定数へ一元化（= --shell-ease-emphasis / --shell-ease-back-in）
  const easing = cameraScale > 1 ? EASE_EMPHASIS : EASE_BACK_IN;
  return {
    transform: `translate3d(${offsetXPct}%, 0, 0) scale(${cameraScale})`,
    transition: `transform ${transitionMs}ms ${easing}`,
    transformOrigin: '50% 50%'
  };
}

function resolveActivePortraitSide({
  step,
  lines,
  lineIndex,
  leftSrc,
  rightSrc
}: {
  step: any;
  lines: any[] | null;
  lineIndex: number;
  leftSrc?: string;
  rightSrc?: string;
}): 'left' | 'right' | 'none' {
  if (!lines || lines.length === 0) {
    // lines無し（旧textのみ）の場合は、片側のみ存在するならそれを採用。両方あるなら誤判定を避けてnone。
    if (leftSrc && !rightSrc) return 'left';
    if (!leftSrc && rightSrc) return 'right';
    return 'none';
  }

  const map = step?.speakerSideMap ?? {};
  let current: 'left' | 'right' | 'none' | null = null;
  const max = Math.min(Math.max(0, lineIndex), lines.length - 1);
  for (let i = 0; i <= max; i += 1) {
    const ln = lines[i];
    const explicit = ln?.activePortraitSide;
    if (explicit === 'left' || explicit === 'right' || explicit === 'none') {
      current = explicit;
      continue;
    }
    const speakerKey = ln?.speakerKey ?? ln?.speaker?.name;
    const mapped = typeof speakerKey === 'string' ? map?.[speakerKey] : undefined;
    if (mapped === 'left' || mapped === 'right') {
      current = mapped;
    }
  }

  if (current) return current;
  if (leftSrc && !rightSrc) return 'left';
  if (!leftSrc && rightSrc) return 'right';
  return 'none';
}

